import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Award, BadgeCheck, MapPin, Star } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { SERVICE_NAMES, serviceImage, serviceTint } from '@/lib/domain';
import { formatDate } from '@/lib/format';
import { isVideoUrl } from '@/lib/media';
import { Avatar, Lightbox, PageHeader, Stars } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './ProviderProfile.module.css';

type ProviderData = {
  id: string;
  full_name: string;
  city: string;
  rating: number;
  experience_years: number;
  description: string;
  services: string[];
  is_approved: boolean;
};

type WorkPhoto = { id: string; photo_url: string };
type Review = {
  rating: number;
  review_comment: string | null;
  created_at: string;
  client_name: string;
};

export default function ProviderProfile() {
  const { id } = useParams<{ id: string }>();
  const [provider, setProvider] = useState<ProviderData | null>(null);
  const [workPhotos, setWorkPhotos] = useState<WorkPhoto[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let active = true;

    void (async () => {
      const { data: providerRow } = await supabase
        .from('providers')
        .select('id, rating, experience_years, description, is_approved, profiles(full_name, city)')
        .eq('id', id)
        .maybeSingle();

      const { data: application } = await supabase
        .from('provider_applications')
        .select('city, services, experience_years, description')
        .eq('user_id', id)
        .eq('status', 'approved')
        .maybeSingle();

      const { data: linkedServices } = await supabase
        .from('provider_services')
        .select('services(name)')
        .eq('provider_id', id);

      const serviceNames =
        (linkedServices ?? [])
          .map((row: any) => row.services?.name)
          .filter(Boolean) as string[];

      const servicesList = serviceNames.length > 0 ? serviceNames : (application?.services ?? []);

      const { data: photos } = await supabase
        .from('provider_work_photos')
        .select('id, photo_url')
        .eq('provider_id', id)
        .order('created_at', { ascending: false });

      const { data: reviewRows } = await supabase
        .from('bookings')
        .select('rating, review_comment, created_at, profiles!client_id(full_name)')
        .eq('provider_id', id)
        .eq('status', 'completed')
        .not('rating', 'is', null)
        .order('created_at', { ascending: false })
        .limit(20);

      if (!active) return;

      const mappedReviews: Review[] = (reviewRows ?? []).map((row: any) => ({
        rating: row.rating,
        review_comment: row.review_comment ?? null,
        created_at: row.created_at,
        client_name: row.profiles?.full_name ?? 'Client',
      }));

      // The live average from actual reviews wins over the cached column.
      const liveRating =
        mappedReviews.length > 0
          ? Math.round(
              (mappedReviews.reduce((sum, review) => sum + review.rating, 0) /
                mappedReviews.length) *
                10
            ) / 10
          : 0;

      if (providerRow) {
        setProvider({
          id: providerRow.id,
          full_name: (providerRow.profiles as any)?.full_name ?? 'Prestataire',
          city: (providerRow.profiles as any)?.city || application?.city || '',
          rating: liveRating,
          experience_years: providerRow.experience_years || application?.experience_years || 0,
          description: providerRow.description || application?.description || '',
          services: servicesList,
          is_approved: Boolean(providerRow.is_approved),
        });
      }

      setWorkPhotos(photos ?? []);
      setReviews(mappedReviews);
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [id]);

  if (loading) return <Spinner block label="Chargement du profil..." />;

  if (!provider) {
    return (
      <div>
        <PageHeader title="Profil du prestataire" back="/app/providers" />
        <p className={styles.notFound}>Prestataire introuvable.</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Profil du prestataire" back="/app/providers" />

      <div className={styles.body}>
        <div className={styles.mainCol}>
          <section className={styles.profileCard}>
            <div className={styles.cover} aria-hidden="true" />
            <span className={styles.avatarRing}>
              <Avatar name={provider.full_name} size={96} tone="orange" />
            </span>
            <h1 className={styles.name}>{provider.full_name}</h1>

            {provider.is_approved ? (
              <p className={styles.verifiedBadge}>
                <BadgeCheck size={16} /> Prestataire vérifié
              </p>
            ) : null}

            {provider.rating > 0 ? (
              <p className={styles.rating}>
                <Star size={18} color="#FFB21A" fill="#FFB21A" />
                {provider.rating.toFixed(1)}
                <span className={styles.ratingCount}>· {reviews.length} avis</span>
              </p>
            ) : (
              <p className={styles.noRating}>Pas encore d’avis</p>
            )}

            <div className={styles.metaRow}>
              {provider.city ? (
                <span className={styles.meta}>
                  <MapPin size={16} /> {provider.city}
                </span>
              ) : null}
              {provider.experience_years > 0 ? (
                <span className={styles.meta}>
                  <Award size={16} /> {provider.experience_years} ans
                  d&apos;expérience
                </span>
              ) : null}
            </div>

            <Link
              className="btn btn--primary btn--block"
              to={`/app/new-request?provider=${provider.id}${
                provider.services[0] ? `&service=${encodeURIComponent(provider.services[0])}` : ''
              }`}
            >
              Demander ce prestataire
            </Link>
          </section>

        </div>

        <div className={styles.sideCol}>
          {provider.description ? (
            <section className={styles.section}>
              <h2 className="section-title">À propos</h2>
              <p className={styles.description}>{provider.description}</p>
            </section>
          ) : null}

          {provider.services.length > 0 ? (
            <section className={styles.section}>
              <h2 className="section-title">Services offerts</h2>
              <div className={styles.tagRow}>
                {provider.services.map((service) => (
                  <span key={service} className={styles.serviceTag}>
                    {SERVICE_NAMES.some((name) => name === service) ? (
                      <span
                        className={styles.serviceTagArt}
                        style={{ background: serviceTint(service) }}
                      >
                        <img src={serviceImage(service)} alt="" />
                      </span>
                    ) : null}
                    {service}
                  </span>
                ))}
              </div>
            </section>
          ) : null}
          <section className={styles.section}>
            <h2 className="section-title">Photos et vidéos de travail</h2>
            {workPhotos.length === 0 ? (
              <div className={styles.emptyPanel}>
                <img src="/assets/illustrations/sparkles.png" alt="" />
                <p>Ce prestataire n’a pas encore ajouté de photos ni de vidéos de ses réalisations.</p>
              </div>
            ) : (
              <div className={styles.photoGrid}>
                {workPhotos.map((photo) =>
                  isVideoUrl(photo.photo_url) ? (
                    <video
                      key={photo.id}
                      className={`${styles.photo} ${styles.video}`}
                      src={photo.photo_url}
                      preload="metadata"
                      controls
                      playsInline
                    />
                  ) : (
                    <button
                      key={photo.id}
                      type="button"
                      className={styles.photoBtn}
                      onClick={() => setLightbox(photo.photo_url)}
                    >
                      <img className={styles.photo} src={photo.photo_url} alt="Réalisation" />
                    </button>
                  )
                )}
              </div>
            )}
          </section>

          <section className={styles.section}>
            <h2 className="section-title">Avis clients ({reviews.length})</h2>
            {reviews.length === 0 ? (
              <div className={styles.emptyPanel}>
                <img src="/assets/illustrations/star.png" alt="" />
                <p>Pas encore d’avis. Les avis apparaissent après chaque mission terminée.</p>
              </div>
            ) : (
              <ul className={styles.reviewList}>
                {reviews.map((review, index) => (
                  <li key={index} className={styles.reviewCard}>
                    <div className={styles.reviewHead}>
                      <Stars value={review.rating} size={14} />
                      <span className={styles.reviewClient}>{review.client_name}</span>
                      <span className={styles.reviewDate}>{formatDate(review.created_at)}</span>
                    </div>
                    {review.review_comment ? (
                      <p className={styles.reviewComment}>{review.review_comment}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
